import { useEffect } from "react"

// react hooks

export const useEventListener = <K extends keyof DocumentEventMap>(event: K, callback: (e: DocumentEventMap[K]) => void, deps?: React.DependencyList) => {
  return useEffect(() => {
    document.addEventListener(event, callback);
    return () => document.removeEventListener(event, callback);
    // eslint-disable-next-line
  }, deps || []);
}

export const useAsyncEffect = (callback: () => Promise<void>, deps: React.DependencyList = []) => {
  return useEffect(() => {
    callback();
    // eslint-disable-next-line
  }, deps);
}

// util functions

export const escapeHtml = (str: string) => str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');

export const getRandomString = (length: number) => Array.from({ length }, () => Math.random() < 0.5 ? Math.random().toString(36).charAt(2) : Math.random().toString(36).charAt(2).toUpperCase()).join('');

export const splitByUpperCase = (str: string) => str.replace(/([A-Z])/g, ' $1').trim();

export const isValidServiceName = (name: string) => name.trim().length > 0 && /^[a-zA-Z0-9_]+$/.test(name);

export const isValidTypeString = (type: 'string' | 'number' | 'list' | 'boolean' | 'map', value: string) => {
  if (type === 'string') return true;
  if (type === 'number') return value.length > 0 && !isNaN(Number(value));
  if (type === 'boolean') return value.toLowerCase() === 'true' || value.toLowerCase() === 'false';
  try {
    const obj = JSON.parse(value);
    if (type === 'list') return Array.isArray(obj);
    return typeof obj === 'object' && !Array.isArray(obj);
  } catch (err) { return false; }
}

export const hasDuplicates = (objects: any[], key: string) => objects.some((_, i, arr) => arr.filter(obj => obj[key] === arr[i][key]).length >= 2);

export const getTimeFromSeconds = (seconds: number) => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  return `${days}:${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
};


// get attributes of all data
export const getAttributes = (data: any[]) => {
  let attributes: { name: string, type: 'string' | 'number' | 'list' | 'boolean' | 'map' }[] = [];
  for (const item of data) {
    for (const key in item) {
      if (!attributes.find(attr => attr.name === key)) {
        attributes.push({ name: key, type: (
          typeof item[key] === 'object' ? (Array.isArray(item[key]) ? 'list' : 'map') 
            : typeof item[key] === 'number' ? 'number'
            : typeof item[key] === 'boolean' ? 'boolean'
            : 'string'
        ) });
      }
    }
  }
  return attributes;
}

export const getStandardTime = (date: Date) => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`
}

export const getDateString = (date: Date) => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

export const getSizeString = (size: number) => {
  if (size < 1024) return `${size} B`;
  if (size < 1048576) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1073741824) return `${(size / 1048576).toFixed(1)} MB`;
  return `${(size / 1073741824).toFixed(1)} GB`;
}

export const blobToBase64 = (blob: Blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => resolve(reader.result);
  reader.onerror = (error) => reject(error);
  reader.readAsDataURL(blob);
});

export const base64ToBlob = (base64: string, mimeType: string) => {
  const binaryString = atob(base64);
  const byteArray = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
      byteArray[i] = binaryString.charCodeAt(i);
  }
  return new Blob([byteArray], { type: mimeType });
}

// csv handling

export const convertToCSV = (data: any[], attributes: { name: string, type: string }[]): string => {
  if (!data || data.length === 0) return '';

  // Extract headers from object keys
  const headers = attributes.map((attr) => attr.name);
  const csvRows = [
    headers.join(','),
    ...data.map((row) =>
      headers.map((field) => JSON.stringify(row[field] || '')).join(',')
    ),
  ];
  return csvRows.join('\n');
};